import { ClipboardCheck, FileText, Flag, Users } from 'lucide-react';
import { useCallback, useMemo, useReducer } from 'react';

import {
  type AuxiliarDisponible,
  type ParticipantePrivadoDisponible,
  type SolicitanteDisponible,
  type SupervisorDisponible,
} from '@/entities/proyecto';
import { isIsoDate, rangoOk } from '@/shared/lib';
import { type StepStatus, type WizardStep } from '@/shared/ui';

import type { PasoDatosGeneralesValues } from './paso-datos-generales.schema';

export type ProyectoWizardStepKey = 'datos-generales' | 'supervisores' | 'fases' | 'controles';

export const STEP_ORDER: ReadonlyArray<ProyectoWizardStepKey> = [
  'datos-generales',
  'supervisores',
  'fases',
  'controles',
];

/** Pasos que preceden a `step` en el orden del wizard (los ya completados). */
export function pasosAnterioresA(
  step: ProyectoWizardStepKey,
): ReadonlyArray<ProyectoWizardStepKey> {
  const idx = STEP_ORDER.indexOf(step);
  return idx > 0 ? STEP_ORDER.slice(0, idx) : [];
}

/**
 * Shape del supervisor seleccionado dentro del wizard. Es exactamente
 * `SupervisorDisponible` (item devuelto por `GET /proyectos/supervisores-disponibles`):
 * el dialog "Agregar supervisor" guarda el item tal como vino del
 * backend para que cuando el wizard envíe el proyecto la asignación
 * use el mismo `id` que devolvió ese endpoint. Cardinalidad 1 — el
 * POST `/api/v1/proyectos` acepta `supervisorId?: number` (opcional).
 */
export type ProyectoWizardSupervisor = SupervisorDisponible;

/**
 * Shape del auxiliar seleccionado dentro del wizard. Cardinalidad N —
 * el POST `/api/v1/proyectos` acepta `auxiliarIds?: number[]`. Igual
 * que con el supervisor, se guarda el item completo del catálogo
 * `usuarios-disponibles?rol=Auxiliar` para reutilizar shape al
 * pintar la lista y al confirmar el guardado.
 */
export type ProyectoWizardAuxiliar = AuxiliarDisponible;

/**
 * Shape del participante privado seleccionado dentro del wizard.
 * Cardinalidad N — el POST `/api/v1/proyectos` acepta
 * `participantePrivadoIds?: number[]` (líder + auxiliares de campo mezclados).
 * Se guarda el item completo del catálogo `usuarios-disponibles?rol=ParticipantePrivado`
 * —incluyendo `rolNombre`— para pintar el nivel en la tabla del paso 2. El
 * backend deriva líder/auxiliar del rol global; el payload solo manda `id`.
 */
export type ProyectoWizardParticipantePrivado = ParticipantePrivadoDisponible;

/**
 * Shape del solicitante seleccionado dentro del wizard. Cardinalidad N —
 * el POST `/api/v1/proyectos` acepta `solicitanteIds?: number[]`. El
 * solicitante es un actor externo de solo lectura; se guarda el item
 * completo del catálogo `usuarios-disponibles?rol=Solicitante` para
 * reutilizar shape al pintar la lista y al confirmar el guardado.
 */
export type ProyectoWizardSolicitante = SolicitanteDisponible;

/**
 * Fase dinámica del proyecto (ADR-032). Las fases dejaron de ser fijas
 * (`Construcción`/`Explotación`): el usuario define N fases con nombre
 * libre y un `orden` 1..n. Cada subfase referencia su fase por
 * `faseLocalId` (id efímero), no por tipo.
 *
 * `serverId` solo se setea al hidratar el wizard para editar un proyecto
 * existente (id real de la fase en el backend); en creación queda
 * `undefined`. `localId` es efímero (React keys / asociación con subfases)
 * y nunca se envía al backend.
 */
export interface ProyectoWizardFase {
  localId: string;
  serverId?: number;
  nombre: string;
  /** Posición 1..n; determina el orden de render y se envía al backend. */
  orden: number;
}

/**
 * Item (control) asociado a una subfase (ADR-032). El control unifica
 * condición mínima e indicador de desempeño; el array al que pertenece
 * (`condiciones` / `indicadores` de la subfase) determina su `tipoControl`
 * al serializar. Los controles se definen **inline** (texto libre del
 * proyecto), no por referencia a un catálogo global.
 *
 *  - Condición:  `descripcion` (requerido) + `codigo?`.
 *  - Indicador:  `nombre` (requerido) + `unidadMedida` + `metaValor` +
 *                `direccionMeta` + `codigo?`.
 *
 * `catalogo`/`origen` quedan como vestigio opcional (proyectos legacy);
 * los controles inline llevan `origen: 'manual'` o lo omiten.
 */
export type ProyectoWizardItemOrigen = 'catalogo' | 'manual';

export type ProyectoWizardDireccionMeta = 'MayorOIgual' | 'MenorOIgual';

export interface ProyectoWizardSubfaseItemCatalogoRef {
  /** `id` del item original en el catálogo (CondicionMinimaItem o IndicadorDesempenoItem). */
  itemId: number;
  /** Código canónico del ítem (p. ej. `O-C-AE-1`). */
  codigo: string;
  /**
   * Categoría del catálogo (display en el sheet de asignación). OPCIONAL:
   * al hidratar el wizard desde un proyecto existente (edición) el árbol
   * de respuesta no expone la categoría del ítem; downstream solo se usan
   * `itemId` (serialización) y `codigo` (chip), así que se degrada sin
   * romper. Al asignar desde el catálogo sí viene completa.
   */
  categoria?: { id: number; codigo: string; nombre: string };
}

export interface ProyectoWizardSubfaseItem {
  localId: string;
  /**
   * `id` real del ítem en el backend (`ProyectoCondicion.id` /
   * `ProyectoIndicador.id`). Solo se setea al hidratar el wizard para
   * editar un proyecto existente; en creación queda `undefined`.
   */
  serverId?: number;
  /**
   * `orden` real del control en su subfase (único por subfase a través de
   * AMBOS tipos, ADR-032). Se setea al hidratar (orden del server) y al
   * persistir con sync-on-add (orden que asignó el POST). `undefined` mientras
   * el control no se persiste: el finalize le asigna uno nuevo (max+1) SIN
   * renumerar los ya persistidos (evita `CONTROL_ORDEN_DUPLICADO` por reorden).
   */
  orden?: number;
  /**
   * Categoría agrupadora del control (ADR-032b). Obligatoria para persistir un
   * control nuevo (sync-on-add) — la asigna el modal de creación; se hidrata
   * desde `control.categoriaId` al retomar un borrador.
   */
  categoriaId?: number;
  nombre: string;
  descripcion?: string | null;
  /** Código/etiqueta contractual opcional del control (≤50). */
  codigo?: string;
  unidadMedida?: string;
  metaValor?: string;
  /**
   * Dirección de la meta para indicadores. Por default `MayorOIgual`
   * (cumplir cuando el valor real ≥ meta). `MenorOIgual` aplica a
   * métricas tipo IRI/rugosidad donde se cumple si valor ≤ meta.
   */
  direccionMeta?: ProyectoWizardDireccionMeta;
  /** Default `'manual'` cuando se omite (ítems pre-catálogo). */
  origen?: ProyectoWizardItemOrigen;
  catalogo?: ProyectoWizardSubfaseItemCatalogoRef;
}

/**
 * Subfase elegida dentro del wizard. Pertenece a una fase dinámica vía
 * `faseLocalId`. Las fechas viven a nivel subfase (no fase) y deben
 * caer dentro del rango del proyecto.
 *
 * `localId` (tanto en la subfase como en cada item) es un identificador
 * efímero para React keys y operaciones de borrado dentro del wizard;
 * nunca se envía al backend.
 */
export interface ProyectoWizardSubfase {
  localId: string;
  /**
   * `id` real de la subfase en el backend (`ProyectoSubfase.id`). Solo se
   * setea al hidratar el wizard para editar; en creación queda `undefined`.
   */
  serverId?: number;
  /** Fase dinámica a la que pertenece (`ProyectoWizardFase.localId`). */
  faseLocalId: string;
  nombre: string;
  descripcion: string | null;
  /**
   * Fechas de la subfase en formato `YYYY-MM-DD`. Se serializan a ISO
   * UTC al armar el payload. Deben estar contenidas en el rango del
   * proyecto y `fechaFinEstimada > fechaInicio`. Pueden quedar como `''`
   * mientras el usuario las completa; el wizard no permite avanzar con
   * vacíos.
   */
  fechaInicio: string;
  fechaFinEstimada: string;
  /**
   * Items de la subfase. Temporalmente sin UI (ver nota en
   * `ProyectoWizardSubfaseItem`); los arrays quedan vacíos hasta que se
   * reconstruya el paso de condiciones/indicadores.
   */
  condiciones: ReadonlyArray<ProyectoWizardSubfaseItem>;
  indicadores: ReadonlyArray<ProyectoWizardSubfaseItem>;
}

export interface ProyectoWizardState {
  currentStep: ProyectoWizardStepKey;
  completed: ReadonlyArray<ProyectoWizardStepKey>;
  datosGenerales: Partial<PasoDatosGeneralesValues>;
  /** Único supervisor responsable. `null` = sin supervisor. */
  supervisor: ProyectoWizardSupervisor | null;
  /** Auxiliares asignados al crear el proyecto (cardinalidad N). */
  auxiliares: ReadonlyArray<ProyectoWizardAuxiliar>;
  /** Participantes privados asignados al crear el proyecto (cardinalidad N). */
  participantesPrivados: ReadonlyArray<ProyectoWizardParticipantePrivado>;
  /** Solicitantes asignados al crear el proyecto (cardinalidad N, solo lectura). */
  solicitantes: ReadonlyArray<ProyectoWizardSolicitante>;
  /** Fases dinámicas del proyecto, ordenadas por `orden`. */
  fases: ReadonlyArray<ProyectoWizardFase>;
  subfases: ReadonlyArray<ProyectoWizardSubfase>;
}

type Action =
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'goTo'; step: ProyectoWizardStepKey }
  | { type: 'completeAndAdvance'; step: ProyectoWizardStepKey }
  | { type: 'setDatosGenerales'; values: PasoDatosGeneralesValues }
  | { type: 'setSupervisor'; supervisor: ProyectoWizardSupervisor | null }
  | { type: 'setAuxiliares'; auxiliares: ReadonlyArray<ProyectoWizardAuxiliar> }
  | {
      type: 'setParticipantesPrivados';
      participantesPrivados: ReadonlyArray<ProyectoWizardParticipantePrivado>;
    }
  | { type: 'setSolicitantes'; solicitantes: ReadonlyArray<ProyectoWizardSolicitante> }
  | { type: 'addFase'; nombre: string }
  | { type: 'renameFase'; localId: string; nombre: string }
  | { type: 'removeFase'; localId: string }
  | { type: 'moveFase'; localId: string; direction: 'up' | 'down' }
  | { type: 'setSubfases'; subfases: ReadonlyArray<ProyectoWizardSubfase> };

const initialState: ProyectoWizardState = {
  currentStep: 'datos-generales',
  completed: [],
  datosGenerales: {},
  supervisor: null,
  auxiliares: [],
  participantesPrivados: [],
  solicitantes: [],
  fases: [],
  subfases: [],
};

/** Id efímero — mismo mecanismo que usan los dialogs del wizard. */
function newLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Reasigna `orden` 1..n respetando el orden del array. */
function renumerar(fases: ReadonlyArray<ProyectoWizardFase>): ProyectoWizardFase[] {
  return fases.map((f, i) => ({ ...f, orden: i + 1 }));
}

/**
 * Una subfase tiene fechas válidas cuando ambas están seteadas,
 * `fin > inicio` (estricto) y ambas caen dentro del rango del proyecto.
 * Delegamos al helper compartido `rangoOk`.
 */
function fechasSubfaseValidas(
  subfase: ProyectoWizardSubfase,
  proyecto: Partial<PasoDatosGeneralesValues>,
): boolean {
  return rangoOk(subfase.fechaInicio, subfase.fechaFinEstimada, {
    min: proyecto.fechaInicio,
    max: proyecto.fechaFinEstimada,
    finEstrictamenteMayor: true,
  });
}

/**
 * ¿Dos subfases consecutivas se solapan? Hay solapamiento cuando la
 * `siguiente` inicia en o antes del fin de la `anterior` (van en serie).
 * Si alguna fecha no es ISO válida, no se considera solapamiento (esas se
 * validan aparte).
 */
function subfasesSolapan(
  anterior: Pick<ProyectoWizardSubfase, 'fechaFinEstimada'>,
  siguiente: Pick<ProyectoWizardSubfase, 'fechaInicio'>,
): boolean {
  const fin = anterior.fechaFinEstimada;
  const inicio = siguiente.fechaInicio;
  return isIsoDate(fin) && isIsoDate(inicio) && inicio <= fin;
}

/**
 * Devuelve `true` si las subfases (en el orden recibido) están ordenadas
 * cronológicamente sin solapamientos: cada subfase debe iniciar
 * estrictamente después del fin de la anterior. Ignora subfases con
 * fechas inválidas (esas se validan aparte).
 */
function subfasesEnOrden(subs: ReadonlyArray<ProyectoWizardSubfase>): boolean {
  for (let i = 1; i < subs.length; i++) {
    if (subfasesSolapan(subs[i - 1]!, subs[i]!)) return false;
  }
  return true;
}

function nextStep(current: ProyectoWizardStepKey): ProyectoWizardStepKey {
  const idx = STEP_ORDER.indexOf(current);
  return STEP_ORDER[Math.min(idx + 1, STEP_ORDER.length - 1)] ?? current;
}

function prevStep(current: ProyectoWizardStepKey): ProyectoWizardStepKey {
  const idx = STEP_ORDER.indexOf(current);
  return STEP_ORDER[Math.max(idx - 1, 0)] ?? current;
}

function withCompleted(
  list: ReadonlyArray<ProyectoWizardStepKey>,
  step: ProyectoWizardStepKey,
): ReadonlyArray<ProyectoWizardStepKey> {
  return list.includes(step) ? list : [...list, step];
}

function reducer(state: ProyectoWizardState, action: Action): ProyectoWizardState {
  switch (action.type) {
    case 'next':
      return { ...state, currentStep: nextStep(state.currentStep) };
    case 'prev':
      return { ...state, currentStep: prevStep(state.currentStep) };
    case 'goTo':
      return { ...state, currentStep: action.step };
    case 'completeAndAdvance':
      return {
        ...state,
        completed: withCompleted(state.completed, action.step),
        currentStep: nextStep(action.step),
      };
    case 'setDatosGenerales':
      return { ...state, datosGenerales: action.values };
    case 'setSupervisor':
      return { ...state, supervisor: action.supervisor };
    case 'setAuxiliares':
      return { ...state, auxiliares: action.auxiliares };
    case 'setParticipantesPrivados':
      return { ...state, participantesPrivados: action.participantesPrivados };
    case 'setSolicitantes':
      return { ...state, solicitantes: action.solicitantes };
    case 'addFase': {
      const fase: ProyectoWizardFase = {
        localId: newLocalId(),
        nombre: action.nombre,
        orden: state.fases.length + 1,
      };
      return { ...state, fases: [...state.fases, fase] };
    }
    case 'renameFase':
      return {
        ...state,
        fases: state.fases.map((f) =>
          f.localId === action.localId ? { ...f, nombre: action.nombre } : f,
        ),
      };
    case 'removeFase':
      // Borrar una fase arrastra sus subfases (cascada) y renumera el resto.
      return {
        ...state,
        fases: renumerar(state.fases.filter((f) => f.localId !== action.localId)),
        subfases: state.subfases.filter((s) => s.faseLocalId !== action.localId),
      };
    case 'moveFase': {
      const idx = state.fases.findIndex((f) => f.localId === action.localId);
      if (idx < 0) return state;
      const swapWith = action.direction === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= state.fases.length) return state;
      const next = [...state.fases];
      [next[idx], next[swapWith]] = [next[swapWith]!, next[idx]!];
      return { ...state, fases: renumerar(next) };
    }
    case 'setSubfases':
      return { ...state, subfases: action.subfases };
    default:
      return state;
  }
}

function statusOf(
  step: ProyectoWizardStepKey,
  current: ProyectoWizardStepKey,
  completed: ReadonlyArray<ProyectoWizardStepKey>,
): StepStatus {
  if (completed.includes(step)) return 'completed';
  if (step === current) return 'active';
  return 'pending';
}

export interface UseProyectoWizardReturn {
  currentStep: ProyectoWizardStepKey;
  isFirstStep: boolean;
  isLastStep: boolean;
  datosGenerales: Partial<PasoDatosGeneralesValues>;
  supervisor: ProyectoWizardSupervisor | null;
  auxiliares: ReadonlyArray<ProyectoWizardAuxiliar>;
  participantesPrivados: ReadonlyArray<ProyectoWizardParticipantePrivado>;
  solicitantes: ReadonlyArray<ProyectoWizardSolicitante>;
  fases: ReadonlyArray<ProyectoWizardFase>;
  subfases: ReadonlyArray<ProyectoWizardSubfase>;
  /**
   * `true` cuando el paso 3 (estructura) está completo y se puede avanzar
   * al paso 4:
   *   - ≥1 fase. Cada fase con ≥1 subfase.
   *   - Cada subfase con fechas válidas dentro del rango del proyecto.
   *   - Subfases consecutivas en orden DENTRO de cada fase (no se solapan).
   *     Las fases entre sí son independientes (pueden compartir fechas).
   */
  fasesListas: boolean;
  /** Motivos por los que `fasesListas` está en `false` (paso 3). */
  razonesFases: ReadonlyArray<string>;
  /**
   * `true` cuando el árbol está listo para guardar/activar: `fasesListas`
   * + cada subfase con ≥1 control (condición o indicador).
   */
  puedeGuardar: boolean;
  /** Motivos por los que `puedeGuardar` está en `false` (paso 4). */
  razonesParaGuardar: ReadonlyArray<string>;
  /** localIds de las subfases con fechas inválidas o fuera de rango. */
  subfasesConFechasInvalidas: ReadonlyArray<string>;
  /** localIds de las subfases que se solapan con la subfase previa de su fase. */
  subfasesSolapadas: ReadonlyArray<string>;
  /** localIds de las subfases sin ningún control (paso 4). */
  subfasesSinControles: ReadonlyArray<string>;
  steps: WizardStep[];
  next: () => void;
  prev: () => void;
  completeAndAdvance: (step: ProyectoWizardStepKey) => void;
  setDatosGenerales: (values: PasoDatosGeneralesValues) => void;
  setSupervisor: (supervisor: ProyectoWizardSupervisor | null) => void;
  setAuxiliares: (auxiliares: ReadonlyArray<ProyectoWizardAuxiliar>) => void;
  setParticipantesPrivados: (
    participantesPrivados: ReadonlyArray<ProyectoWizardParticipantePrivado>,
  ) => void;
  setSolicitantes: (solicitantes: ReadonlyArray<ProyectoWizardSolicitante>) => void;
  addFase: (nombre: string) => void;
  renameFase: (localId: string, nombre: string) => void;
  removeFase: (localId: string) => void;
  moveFase: (localId: string, direction: 'up' | 'down') => void;
  setSubfases: (subfases: ReadonlyArray<ProyectoWizardSubfase>) => void;
}

/**
 * State del wizard de proyecto. Mantiene paso actual, pasos completados
 * y valores parciales por paso. La validación por paso ocurre en cada
 * widget; este hook sólo expone transiciones y datos. Reusa el componente
 * `WizardLayout` de `shared/ui`.
 *
 * `initial` permite hidratar el wizard al editar un proyecto existente
 * (datos generales, fases dinámicas, subfases/ítems con `serverId`,
 * equipo). En creación se invoca sin argumentos y arranca desde
 * `initialState` (todo vacío).
 */
export function useProyectoWizard(initial?: Partial<ProyectoWizardState>): UseProyectoWizardReturn {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...initial });

  const next = useCallback(() => dispatch({ type: 'next' }), []);
  const prev = useCallback(() => dispatch({ type: 'prev' }), []);
  const completeAndAdvance = useCallback(
    (step: ProyectoWizardStepKey) => dispatch({ type: 'completeAndAdvance', step }),
    [],
  );
  const setDatosGenerales = useCallback(
    (values: PasoDatosGeneralesValues) => dispatch({ type: 'setDatosGenerales', values }),
    [],
  );
  const setSupervisor = useCallback(
    (supervisor: ProyectoWizardSupervisor | null) =>
      dispatch({ type: 'setSupervisor', supervisor }),
    [],
  );
  const setAuxiliares = useCallback(
    (auxiliares: ReadonlyArray<ProyectoWizardAuxiliar>) =>
      dispatch({ type: 'setAuxiliares', auxiliares }),
    [],
  );
  const setParticipantesPrivados = useCallback(
    (participantesPrivados: ReadonlyArray<ProyectoWizardParticipantePrivado>) =>
      dispatch({ type: 'setParticipantesPrivados', participantesPrivados }),
    [],
  );
  const setSolicitantes = useCallback(
    (solicitantes: ReadonlyArray<ProyectoWizardSolicitante>) =>
      dispatch({ type: 'setSolicitantes', solicitantes }),
    [],
  );
  const addFase = useCallback((nombre: string) => dispatch({ type: 'addFase', nombre }), []);
  const renameFase = useCallback(
    (localId: string, nombre: string) => dispatch({ type: 'renameFase', localId, nombre }),
    [],
  );
  const removeFase = useCallback(
    (localId: string) => dispatch({ type: 'removeFase', localId }),
    [],
  );
  const moveFase = useCallback(
    (localId: string, direction: 'up' | 'down') =>
      dispatch({ type: 'moveFase', localId, direction }),
    [],
  );
  const setSubfases = useCallback(
    (subfases: ReadonlyArray<ProyectoWizardSubfase>) => dispatch({ type: 'setSubfases', subfases }),
    [],
  );

  // Subfases agrupadas por fase, preservando el orden del array (que es el
  // orden cronológico que el usuario fue armando dentro de cada fase).
  const subfasesPorFase = useMemo(() => {
    const map = new Map<string, ProyectoWizardSubfase[]>();
    for (const fase of state.fases) map.set(fase.localId, []);
    for (const sub of state.subfases) {
      const lista = map.get(sub.faseLocalId);
      if (lista) lista.push(sub);
    }
    return map;
  }, [state.fases, state.subfases]);

  // Cada fase necesita ≥1 subfase; cada subfase fechas válidas; y las
  // subfases de una misma fase no se solapan. Las fases son calendarios
  // independientes entre sí.
  const fasesListas = useMemo(
    () =>
      state.fases.length > 0 &&
      state.fases.every((fase) => {
        const subs = subfasesPorFase.get(fase.localId) ?? [];
        return (
          subs.length > 0 &&
          subs.every((s) => fechasSubfaseValidas(s, state.datosGenerales)) &&
          subfasesEnOrden(subs)
        );
      }),
    [state.fases, subfasesPorFase, state.datosGenerales],
  );

  // localIds de subfases sin ningún control (ni condición ni indicador).
  // Cada subfase necesita ≥1 control para poder guardar (paso 4).
  const subfasesSinControles = useMemo(
    () =>
      state.subfases
        .filter((s) => s.condiciones.length === 0 && s.indicadores.length === 0)
        .map((s) => s.localId),
    [state.subfases],
  );

  const subfasesConFechasInvalidas = useMemo(
    () =>
      state.subfases
        .filter((s) => !fechasSubfaseValidas(s, state.datosGenerales))
        .map((s) => s.localId),
    [state.subfases, state.datosGenerales],
  );

  const subfasesSolapadas = useMemo(() => {
    const ids: string[] = [];
    for (const subs of subfasesPorFase.values()) {
      for (let i = 1; i < subs.length; i++) {
        if (subfasesSolapan(subs[i - 1]!, subs[i]!)) ids.push(subs[i]!.localId);
      }
    }
    return ids;
  }, [subfasesPorFase]);

  // Paso 4 listo: todas las subfases tienen ≥1 control.
  const puedeGuardar = fasesListas && subfasesSinControles.length === 0;

  // Razones del paso 3 (estructura de fases/subfases).
  const razonesFases = useMemo<string[]>(() => {
    const razones: string[] = [];
    if (state.fases.length === 0) {
      razones.push('Agregue al menos una fase al proyecto.');
      return razones;
    }
    const fasesSinSubfases = state.fases.filter(
      (f) => (subfasesPorFase.get(f.localId) ?? []).length === 0,
    );
    if (fasesSinSubfases.length > 0) {
      razones.push('Cada fase necesita al menos una subfase.');
    }
    if (subfasesConFechasInvalidas.length > 0) {
      razones.push(
        'Hay subfases con fechas inválidas o fuera del rango del proyecto. Revise inicio y fin.',
      );
    }
    if (subfasesSolapadas.length > 0) {
      razones.push('Hay subfases que se solapan dentro de una misma fase. Deben ir en serie.');
    }
    return razones;
  }, [state.fases, subfasesPorFase, subfasesConFechasInvalidas, subfasesSolapadas]);

  // Razones del paso 4 (controles por subfase) — mostradas junto al CTA final.
  const razonesParaGuardar = useMemo<string[]>(() => {
    const razones: string[] = [];
    if (!fasesListas) {
      razones.push('Complete la estructura de fases y subfases del paso anterior.');
    }
    if (subfasesSinControles.length > 0) {
      razones.push(
        'Cada subfase necesita al menos una condición mínima o un indicador de desempeño.',
      );
    }
    return razones;
  }, [fasesListas, subfasesSinControles]);

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        id: 'datos-generales',
        label: 'Paso 1',
        title: 'Datos generales',
        icon: <FileText />,
        status: statusOf('datos-generales', state.currentStep, state.completed),
      },
      {
        id: 'supervisores',
        label: 'Paso 2',
        title: 'Supervisores',
        icon: <Users />,
        status: statusOf('supervisores', state.currentStep, state.completed),
      },
      {
        id: 'fases',
        label: 'Paso 3',
        title: 'Fases y subfases',
        icon: <Flag />,
        status: statusOf('fases', state.currentStep, state.completed),
      },
      {
        id: 'controles',
        label: 'Paso 4',
        title: 'Condiciones e indicadores',
        icon: <ClipboardCheck />,
        status: statusOf('controles', state.currentStep, state.completed),
      },
    ],
    [state.currentStep, state.completed],
  );

  return {
    currentStep: state.currentStep,
    isFirstStep: state.currentStep === STEP_ORDER[0],
    isLastStep: state.currentStep === STEP_ORDER[STEP_ORDER.length - 1],
    datosGenerales: state.datosGenerales,
    supervisor: state.supervisor,
    auxiliares: state.auxiliares,
    participantesPrivados: state.participantesPrivados,
    solicitantes: state.solicitantes,
    fases: state.fases,
    subfases: state.subfases,
    fasesListas,
    razonesFases,
    puedeGuardar,
    razonesParaGuardar,
    subfasesConFechasInvalidas,
    subfasesSolapadas,
    subfasesSinControles,
    steps,
    next,
    prev,
    completeAndAdvance,
    setDatosGenerales,
    setSupervisor,
    setAuxiliares,
    setParticipantesPrivados,
    setSolicitantes,
    addFase,
    renameFase,
    removeFase,
    moveFase,
    setSubfases,
  };
}
